// src/components/superuser/PortfolioProperties.js
//
// All properties under the superuser's client: searchable table with per-property
// status summary + health, row click drills into the property (reuses manager views).

import React, { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Chip,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LocationOffIcon from "@mui/icons-material/LocationOff";
import PropertyContext from "../../contexts/PropertyContext";
import usePortfolioData from "../../hooks/usePortfolioData";

export default function PortfolioProperties(props) {
  const ctx = useContext(PropertyContext);
  const clientId = ctx.selectedClient || props.selectedClient || props.clientId;
  const navigate = useNavigate();
  const { rows, loading } = usePortfolioData(clientId);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? rows.filter(
          (r) =>
            r.displayName.toLowerCase().includes(q) ||
            (r.address || "").toLowerCase().includes(q)
        )
      : rows;
    return [...list].sort((a, b) =>
      a.displayName.localeCompare(b.displayName, undefined, { numeric: true })
    );
  }, [rows, search]);

  const open = (r) => {
    ctx.setSelectedProperty(r.id, r.clientId || clientId);
    navigate("/rooms");
  };

  return (
    <Box sx={{ maxWidth: 1300, mx: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Properties
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <TextField
          size="small"
          placeholder="Search properties…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 260 }}
        />
      </Box>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Property</TableCell>
                <TableCell align="right">Rooms</TableCell>
                <TableCell align="right">Down</TableCell>
                <TableCell align="right">Open WOs</TableCell>
                <TableCell align="right">Issues</TableCell>
                <TableCell align="center">Health</TableCell>
                <TableCell align="center">Map</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {loading ? "Loading properties…" : "No properties found."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((r) => (
                  <TableRow
                    key={r.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => open(r)}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {r.displayName}
                      </Typography>
                      {r.address && (
                        <Typography variant="caption" color="text.secondary">
                          {r.address}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{r.totalRooms || 0}</TableCell>
                    <TableCell align="right">{r.downRooms || 0}</TableCell>
                    <TableCell align="right">{r.openWorkOrders || 0}</TableCell>
                    <TableCell align="right">{r.openIssues || 0}</TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={r.bucket.label}
                        sx={{ bgcolor: r.bucket.color, color: "#fff", fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {r.hasLocation ? (
                        <Typography variant="caption" color="text.secondary">
                          ✓
                        </Typography>
                      ) : (
                        <LocationOffIcon
                          fontSize="small"
                          sx={{ color: "text.disabled" }}
                          titleAccess="No map coordinates set"
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
